<template>
  <CBox data-cy="answer-pendency">
    <OmtTypography
      as="h1-bold"
      mb="24px"
    >
      Responder pendências
    </OmtTypography>

    <OmtCard
      mb="16px"
      bg="#FFAF001A"
      border="0"
      border-radius="8px"
    >
      <OmtTypography
        as="p1"
        color="var(--colors-light-primary-gray1)"
      >
        Todas as pendências devem ser enviadas de uma só vez.
      </OmtTypography>
    </OmtCard>

    <OmtTypography
      as="h3-semi-bold"
      mb="22px"
    >
      Insira os documentos solicitados:
    </OmtTypography>

    <ImageUploader
      :length="limitFiles"
      @change="onImageUploaderChange"
      @uploadStart="$emit('uploadStart')"
      @uploadEnd="$emit('uploadEnd')"
    />

    <CFlex mb="16px">
      <OmtTypography
        as="h3-semi-bold"
        mr="8px"
      >
        Insira as informações solicitadas:
      </OmtTypography>
    </CFlex>

    <CFlex
      flexDirection="column"
      alignItems="flex-end"
      mb="16px"
    >
      <OmtTextarea
        height="160px"
        padding="12px"
        placeholder="Inclua informações relacionadas a(s) pendência(s)."
        :initialValue="obs"
        @change="handleChange"
      />
      <OmtTypography as="p2"> {{ obs.length }}/350 </OmtTypography>
    </CFlex>

    <template v-if="exibirMotivos">
      <CBox
        v-for="(refund, refundIndex) in pendingDocsWithMotives"
        :key="`${refund.ticket}-${refundIndex}`"
        :mb="refundIndex + 1 < pendingDocsWithMotives.length ? '24px' : '0'"
      >
        <CBox data-cy="card-content">
          <CGrid
            :templateColumns="['repeat(auto-fit, minmax(100%, max-content))']"
            justify-content="space-between"
          >
            <OmtCard
              bg="var(--colors-light-primary-light-gray)"
              border="0"
              border-radius="8px"
            >
              <template v-if="refund.requestedValue">
                <CFlex
                  align-items="center"
                  mb="8px"
                >
                  <OmtIcons
                    name="file-invoice-dollar"
                    mr="16px"
                  />
                  <TextTile
                    v-chakra
                    mt="5px"
                    v-bind="{ title: refund.requestedValue, as: 'p1-bold', color: '#4E4E4E' }"
                  />
                </CFlex>

                <CDivider
                  v-if="refund.hasMotives"
                  mt="14px"
                  mb="16px"
                />
              </template>

              <template v-if="refund.hasMotives">
                <CBox mt="8px">
                  <ol class="refund-motives-list">
                    <li
                      v-for="(motivo, motivoIndex) in refund.motives"
                      :key="`${refund.ticket}-${motivoIndex}`"
                    >
                      <OmtTypography
                        as="p1"
                        mb="14px"
                      >
                        {{ motivo }}
                      </OmtTypography>
                    </li>
                  </ol>
                </CBox>
              </template>
            </OmtCard>
          </CGrid>
        </CBox>
      </CBox>
    </template>
  </CBox>
</template>

<script>
import { computed, defineComponent, toRefs } from "vue";

import ImageUploader from "@/components/Molecules/ImageUploader";
import TextTile from "@/components/Molecules/TextTile";
import { REFUND_STATUS } from "@/constants/refundStatus";

function cleanMotives(doc) {
  return (doc.reasonsPendencieRefused || []).filter(motivo => typeof motivo === "string" && motivo.trim().length > 0);
}

export default defineComponent({
  name: "AnswerPendency",
  components: {
    ImageUploader,
    TextTile
  },
  props: {
    pendencyToAnswer: {
      type: Object,
      default: () => ({ docs: [] })
    },
    answer: {
      type: Object,
      default: () => ({ obs: "", pictures: [] })
    },
    filesDescription: {
      type: String,
      default: ""
    },
    limitFiles: {
      type: Number,
      default: 0
    }
  },
  setup(props, { emit }) {
    const { pendencyToAnswer, answer, filesDescription, limitFiles } = toRefs(props);

    const obs = computed({
      get() {
        return (answer.value && answer.value.obs) || "";
      },
      set(value) {
        emit("update:answer", {
          ...(answer.value ?? undefined),
          obs: value || ""
        });
      }
    });

    const pendingDocsWithMotives = computed(() => {
      const docs = (pendencyToAnswer.value && pendencyToAnswer.value.docs) || [];

      return docs
        .map(doc => {
          const motives = cleanMotives(doc);

          return {
            ...doc,
            motives,
            hasMotives: motives.length > 0
          };
        })
        .filter(doc => doc.requestedValue || doc.hasMotives);
    });

    const exibirMotivos = computed(() => pendingDocsWithMotives.value.length > 0);

    const motivesLabel = computed(() => {
      const status = pendencyToAnswer.value && pendencyToAnswer.value.status;
      return status === REFUND_STATUS.PENDING || status === REFUND_STATUS.CANCELLED ? "Pendência:" : "Motivo:";
    });

    function handleChange(value) {
      obs.value = value || "";
    }

    function onImageUploaderChange(pictures) {
      emit("update:answer", {
        ...(answer.value ?? undefined),
        pictures
      });
    }

    return {
      obs,
      filesDescription,
      limitFiles,
      pendingDocsWithMotives,
      exibirMotivos,
      motivesLabel,
      handleChange,
      onImageUploaderChange
    };
  }
});
</script>

<style scoped>
.refund-motives-list {
  margin: 0;
  padding-left: 20px;
}

.refund-motives-list li {
  margin-bottom: 6px;
}
</style>
